/**
 * Landing page for the OAuth redirect URL (/auth/callback).
 *
 * This path is only ever reached in a normal browser, after a user clicks
 * "Add to Zoom" (app.facilitator-tools.com/integrations/zoom or the
 * Marketplace) and allows the app. The in-meeting sign-in uses the Zoom Apps
 * SDK PKCE flow (lib/zoomAuth.ts) and never redirects here.
 *
 * When the web app started the flow it put a signed `state` on the authorize
 * URL; we post it with the OAuth `code` to the API, which connects the Zoom
 * account to the signed-in Facilitator Tools user. That connection is what
 * lets the in-meeting app recognise the host and offer their sessions. A
 * redirect without `state` (an older link, or the Marketplace's own Add
 * button) still installs the app; the user is told to finish from the web.
 */

import { useEffect, useRef, useState } from 'react';
import { completeInstall } from '../lib/api';

const APP_BASE = 'https://app.facilitator-tools.com';

type InstallState = 'none' | 'working' | 'done' | 'failed';

export function AddedToZoomPage() {
  const params = new URLSearchParams(window.location.search);
  const error = params.get('error');
  const errorDescription = params.get('error_description');
  const code = params.get('code');
  const state = params.get('state');

  const [install, setInstall] = useState<InstallState>(code && state ? 'working' : 'none');
  const [zoomName, setZoomName] = useState<string | undefined>();
  const [installError, setInstallError] = useState('');
  const startedRef = useRef(false);

  useEffect(() => {
    if (!code || !state || startedRef.current) return;
    startedRef.current = true;
    // The code is single-use; drop it from the address bar so a reload
    // doesn't retry (and fail) the exchange.
    try {
      window.history.replaceState({}, '', window.location.pathname);
    } catch {
      // ignore
    }
    completeInstall(code, state)
      .then((r) => {
        setZoomName(r.zoomDisplayName);
        setInstall('done');
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : '';
        const body = msg.match(/"error":"([^"]+)"/)?.[1];
        setInstallError(body || 'the connection could not be completed');
        setInstall('failed');
      });
  }, [code, state]);

  if (error) {
    return (
      <Shell>
        <h1 className="text-xl font-semibold text-gray-900">The app was not added</h1>
        <p className="text-sm text-gray-600 mt-2">
          Zoom reported: <span className="font-mono">{errorDescription || error}</span>. Nothing has
          changed on your Zoom account.
        </p>
        <a
          href={`${APP_BASE}/integrations/zoom`}
          className="inline-block mt-5 bg-primary-500 text-white px-5 py-2.5 rounded-md text-sm font-semibold hover:bg-primary-700"
        >
          Try again
        </a>
      </Shell>
    );
  }

  if (install === 'working') {
    return (
      <Shell>
        <div className="flex items-center gap-3">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary-500 border-t-transparent" />
          <p className="text-sm text-gray-700">Connecting your Zoom account to Facilitator Tools…</p>
        </div>
      </Shell>
    );
  }

  if (install === 'failed') {
    return (
      <Shell>
        <h1 className="text-xl font-semibold text-gray-900">
          The app is added, but it isn&apos;t connected to your account yet
        </h1>
        <p className="text-sm text-gray-600 mt-2">
          Facilitator Tools is on your Zoom account, but {installError}. Without the connection the
          in-meeting app can&apos;t list your sessions.
        </p>
        <a
          href={`${APP_BASE}/integrations/zoom`}
          className="inline-block mt-5 bg-primary-500 text-white px-5 py-2.5 rounded-md text-sm font-semibold hover:bg-primary-700"
        >
          Try Add to Zoom again
        </a>
        <p className="text-sm text-gray-600 mt-4">
          You can still run a session: open it in Facilitator Tools, choose <b>Link to meeting</b> and
          enter the Zoom meeting ID.
        </p>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-4">
        <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h1 className="text-xl font-semibold text-gray-900">Facilitator Tools is added to your Zoom account</h1>
      {install === 'done' && (
        <p className="text-sm text-gray-600 mt-2">
          Connected{zoomName ? ` as ${zoomName}` : ''}. When you open the app in a meeting you host, it
          lists your sessions.
        </p>
      )}
      {install === 'none' && (
        <p className="text-sm text-gray-600 mt-2">
          To have the in-meeting app list your sessions, add it once from{' '}
          <a href={`${APP_BASE}/integrations/zoom`} className="text-primary-500 font-medium hover:underline">
            Facilitator Tools › Add to Zoom
          </a>{' '}
          while signed in. Otherwise link each session to its meeting from the session designer.
        </p>
      )}
      <p className="text-sm text-gray-600 mt-4">You can close this page. To use the app:</p>
      <ol className="text-sm text-gray-700 mt-3 space-y-3 list-decimal pl-5">
        <li>Start or join a Zoom meeting.</li>
        <li>
          Click <b>Apps</b> in the meeting toolbar and choose <b>Facilitator Tools</b>.
        </li>
        <li>As the host, pick the session to run from the list in your sidebar.</li>
      </ol>
      <p className="text-sm text-gray-600 mt-5">
        No account yet?{' '}
        <a href={`${APP_BASE}/signup`} className="text-primary-500 font-medium hover:underline">
          Create a free one
        </a>
        . Guide:{' '}
        <a
          href="https://facilitator-tools.com/learn/part-5-integrations-zoom-integration/"
          className="text-primary-500 font-medium hover:underline"
        >
          Using Facilitator Tools in Zoom
        </a>
        .
      </p>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-primary-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md bg-white border border-primary-200 rounded-2xl shadow-md p-8">{children}</div>
    </div>
  );
}
